import type { BoardData, Item } from './schema.js';

/** Direct children of `parentId`, ordered by label (decision U2: e.g. the mics of a drum kit). */
export function childrenOf(data: BoardData, parentId: string): Item[] {
  return Object.values(data.items)
    .filter((i) => i.parentId === parentId)
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Walks up the parent chain. Returns the ancestors nearest first and stops at missing parents
 * and cycles, so broken data never loops.
 */
export function ancestorsOf(data: BoardData, item: Item): Item[] {
  const out: Item[] = [];
  const seen = new Set<string>([item.id]);
  let parentId = item.parentId;
  while (parentId !== undefined && !seen.has(parentId)) {
    const parent = data.items[parentId];
    if (parent === undefined) break;
    out.push(parent);
    seen.add(parentId);
    parentId = parent.parentId;
  }
  return out;
}

/**
 * True when an ancestor shows its children as a list, so the item is not drawn individually.
 * Children dragged out of the list (`detached`, OIL-37) are drawn on their own.
 */
export function isListedChild(data: BoardData, item: Item): boolean {
  return item.detached !== true && hasListAncestor(data, item);
}

/** True when an ancestor shows its children as a list (whether or not `item` is detached). */
export function hasListAncestor(data: BoardData, item: Item): boolean {
  return ancestorsOf(data, item).some((a) => a.childDisplay === 'list');
}

/** True when the parent chain of `item` contains `item` itself. */
export function hasParentCycle(data: BoardData, item: Item): boolean {
  let parentId = item.parentId;
  const seen = new Set<string>();
  while (parentId !== undefined && !seen.has(parentId)) {
    if (parentId === item.id) return true;
    seen.add(parentId);
    parentId = data.items[parentId]?.parentId;
  }
  return false;
}

/** Outermost ancestor showing its children as a list – the list box `item` is a row of. */
function listBoxOwner(data: BoardData, item: Item): Item | undefined {
  if (item.detached === true) return undefined;
  let owner: Item | undefined;
  for (const a of ancestorsOf(data, item)) if (a.childDisplay === 'list') owner = a;
  return owner;
}

/**
 * Whether an item is on the plot (OIL-88): in an area, or a row of the list box of a placed
 * parent (e.g. the mics of a placed drum kit), or free (not `unplaced`), or a stand whose items
 * are all such rows (it is shown in their stand column). Only the rest belongs in the unplaced
 * tray. A list row with an area stays placed whatever its owner does, as before areas (R5).
 */
export function isPlaced(data: BoardData, item: Item): boolean {
  if (item.frameId !== null && item.unplaced !== true) return true;
  const owner = listBoxOwner(data, item);
  if (owner !== undefined) return isPlaced(data, owner);
  if (item.unplaced !== true) return true;
  if (item.kind !== 'stand') return false;
  const users = Object.values(data.items).filter((i) => i.mountedOn === item.id);
  return (
    users.length > 0 &&
    users.every((u) => {
      const o = listBoxOwner(data, u);
      return o !== undefined && isPlaced(data, o);
    })
  );
}

/**
 * Items of the unplaced tray (not on the plot, see {@link isPlaced}); ignored ones (U54) only
 * with `includeIgnored`.
 */
export function unplacedItems(data: BoardData, opts: { includeIgnored?: boolean } = {}): Item[] {
  return Object.values(data.items).filter(
    (i) => !isPlaced(data, i) && (opts.includeIgnored === true || i.unplacedIgnored !== true),
  );
}
